import {useCallback, useEffect, useMemo, useState} from 'react';
import useUserInfo, {UserInfoState} from '../../lib/useUserInfo';
import isLocalOrDevEnvironment from '../../lib/isLocalOrDevEnvironment.ts';
import useClientConfig from '../../lib/useClientConfig';
import isNullOrWhiteSpace from '../../lib/isNullOrWhiteSpace';
import {useVoiceAction} from '../../voice/VoiceControlSurfaceContext';

export const FAQBotReadyEventName = 'faqbotready';

export const setBotContext = (userInfo: UserInfoState) => {
    if (
        userInfo.status == 'success' &&
        typeof window.faqbot?.setContext === 'function'
    ) {
        window.faqbot.setContext({
            oid: userInfo.data.oid,
            email: userInfo.data.preferredUsername,
            name: userInfo.data.name
        });
    }
};

const isBotAvailable = () => typeof window.faqbot?.askQuestion === 'function';

const HelpfruitBot = () => {
    const {botSubscriptionKey, isLoading} = useClientConfig();
    const userInfo = useUserInfo();
    // True once the Helpfruit script has loaded and exposed window.faqbot, so the
    // voice action below is only advertised while the widget can actually take a
    // question.
    const [isBotReady, setIsBotReady] = useState(isBotAvailable);

    useEffect(() => {
        const markReady = () => setIsBotReady(isBotAvailable());
        markReady();
        window.addEventListener(FAQBotReadyEventName, markReady);
        return () => {
            window.removeEventListener(FAQBotReadyEventName, markReady);
        };
    }, []);

    // Voice bridge: pose a product how-to question to the help widget. The widget
    // shows its own answer on screen; nothing comes back to the voice assistant.
    useVoiceAction(
        useMemo(
            () => ({
                id: 'ask_product_help',
                label: 'Ask Product Help',
                description:
                    'Open the in-app help widget and pose a "how do I" question about using this application itself, such as "how do I share a thread", "where do I find my scheduled jobs", or "what does this feature do". Use this for questions about features and settings of the application itself, whatever the user calls the product, and not for questions meant for the current assistant. The answer appears in the help widget on screen.',
                parameters: {
                    type: 'object',
                    properties: {
                        question: {
                            type: 'string',
                            description:
                                'The product question, worded as the user asked it.'
                        }
                    },
                    required: ['question'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(args => {
            const question =
                typeof args.question === 'string' ? args.question.trim() : '';
            if (!question) {
                return {message: 'A question is required.'};
            }
            if (!isBotAvailable()) {
                return {
                    status: 'unavailable' as const,
                    message: 'The help widget is not available right now.'
                };
            }

            window.faqbot.open();
            window.faqbot.askQuestion(question);
            return {
                message: `Asked the help widget: "${question}". The answer will appear in the help panel on screen.`,
                data: {question}
            };
        }, []),
        {global: true, enabled: isBotReady}
    );

    // Named rather than inlined into the dependency array: eslint cannot statically check an
    // expression there, so it could not tell whether the array was complete.
    const userOid = userInfo.status === 'success' ? userInfo.data.oid : null;

    // Set up context setting effect - should set on every auth change
    useEffect(() => {
        setBotContext(userInfo);
        const botContextEventListener = () => setBotContext(userInfo);
        window.addEventListener(FAQBotReadyEventName, botContextEventListener);
        return () => {
            window.removeEventListener(
                FAQBotReadyEventName,
                botContextEventListener
            );
        };
        // Keyed on who the user is (status + oid), not on the userInfo object, which is a
        // query result and gets a new identity on every refetch. Depending on it would
        // re-register the listener repeatedly for an identity that has not changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo.status, userOid]);

    // Load the bot and setup cleanup
    useEffect(() => {
        if (isLoading || isNullOrWhiteSpace(botSubscriptionKey)) {
            return;
        }

        // Polyfill nodelist.foreach - required for bot to work
        if (window.NodeList && !NodeList.prototype.forEach) {
            NodeList.prototype.forEach = function (callback, thisArg) {
                thisArg = thisArg || window;
                for (let i = 0; i < this.length; i++) {
                    callback.call(thisArg, this[i], i, this);
                }
            };
        }

        let cleanup = () => {};

        const enableBotOnLocalhost =
            localStorage.getItem('enableBotOnLocalhost') === 'true';
        const shouldHaveBot = !isLocalOrDevEnvironment || enableBotOnLocalhost;

        if (shouldHaveBot) {
            const botScript = document.createElement('script');
            cleanup = () => {
                if (typeof window.faqbot?.unmount == 'function') {
                    window.faqbot.unmount();
                } else {
                    botScript.parentElement?.removeChild(botScript);
                    const botContainer = document.getElementById(
                        `bot-id-${botSubscriptionKey}`
                    ) as HTMLElement;
                    if (botContainer && botContainer.childNodes.length > 0) {
                        botContainer.removeChild(botContainer.childNodes[0]);
                    }
                }
            };
            botScript.type = 'text/javascript';
            botScript.src = `https://portal.helpfruit.com/api/v1/scripts/${botSubscriptionKey}/js/corejs`;
            document.body.appendChild(botScript);
        }

        return () => {
            cleanup();
            setIsBotReady(false);
        };
    }, [botSubscriptionKey, isLoading]);

    return null;
};

export default HelpfruitBot;
